<template>
  <div>
    <collapse
      title="Información de contacto del contratista de servicios profesionales "
    >
      <div class="row bg-white m-2 p-3 rounded">
        <!-- ===================== SELECCIONADOS  ===================== -->
        <div v-if="!isReadOnly" class="my-3 p-2 col-12">
          <custom-separator class="my-4" text="Contratistas Seleccionados" />

          <div
            v-if="model.length > 0"
            class="d-flex justify-content-end align-items-center mt-4 pr-2"
          >
            <div class="d-flex align-items-center mr-3">
              <check-box
                :class="[{ 'text-green': model.length > 0 }]"
                :text="`Deseleccionar Todo (${model.length})`"
                :value="true"
                @input="onDeselectAllSelected"
              />
            </div>
          </div>
        </div>

        <div class="col-12 my-2">
          <slot name="contractor-error" />
        </div>

        <div class="col-12">
          <div v-for="row in model" :key="'sel-' + row.id">
            <provider-row
              :data="{ ...row, checked: true }"
              :is-read-only="isReadOnly"
              @toggle="onToggleFromSelected"
            />
          </div>

          <custom-result-not-found
            v-if="model.length <= 0"
            text="No hay información disponible."
          />
        </div>

        <!-- ===================== BUSCADOR ===================== -->
        <div v-if="!isReadOnly && !isAddMode" class="col my-3">
          <custom-input
            v-model="ssnName"
            icon="fal fa-search color-primary-2"
            left-icon
            placeholder="SSN o Nombre del Proveedor"
            prepend-icon-type="success"
            @input="onSsnInput"
          />
        </div>
        <div v-if="!isReadOnly && !isAddMode" class="col-auto my-3">
          <action-button
            i="fal fa-search"
            text="Buscar"
            variant="success px-3"
            @click="searchByCriteria"
          />
        </div>
        <!-- Formulario de añadir nuevo proveedor -->
        <div v-if="isAddMode" class="col-12">
          <add-provider
            v-model="ssnName"
            @add="onAddNewContractor"
            @cancel="onToggleAddForm"
          />
        </div>

        <!-- ===================== RESULTADOS  ===================== -->
        <div v-if="!isReadOnly && !isAddMode" class="col-12">
          <custom-separator class="my-4" text="Resultados" />

          <div
            v-if="filteredResults.length > 0"
            class="d-flex justify-content-end align-items-center my-3 p-2"
          >
            <div class="d-flex align-items-center mr-3">
              <check-box
                :class="[{ 'text-green': filteredResults.length > 0 }]"
                :text="`Seleccionar Todo (${filteredResults.length})`"
                :value="false"
                @input="onSelectAllResults"
              />
            </div>
          </div>

          <div v-for="row in filteredResults" :key="'res-' + row.id">
            <provider-row
              :data="{ ...row, checked: false }"
              :is-read-only="isReadOnly"
              @toggle="onToggleFromResults"
            />
          </div>

          <div
            v-if="showNoResults"
            class="d-flex justify content-center flex-column align-items-center my-5"
          >
            <custom-result-not-found text="No se encontró Contratista." />

            <a
              v-if="showAddLink"
              class="text-green pointer"
              href="#"
              @click.prevent="onToggleAddForm"
            >
              <i class="fas fa-file-plus mr-2 " />
              Añadir Contratista
            </a>
          </div>
          <pagination
            v-if="showPagination"
            :data="pagination"
            show-text
            @pagination-go-page="goToPageHandler"
          />
        </div>
      </div>
    </collapse>
  </div>
</template>

<script>
//components
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import Collapse from '@/components/common/SimpleCollapse.vue';
import CheckBox from 'CheckBox';
import ProviderRow from '../ProviderRow.vue';
import AddProvider from '../AddProvider.vue';

//constants
const DEFAULT_PAGE_SIZE = 5;

export default {
  name: 'ContractorInfo',
  components: {
    ActionButton,
    CustomInput,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    Collapse,
    CheckBox,
    ProviderRow,
    AddProvider,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    contractors: {
      type: Object,
      default: () => ({
        data: [],
        totalItemCount: 0,
        currentPage: 1,
        count: 0,
        pageCount: 0,
      }),
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    isCompetitionProcessFromMipe: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      ssnName: '',
      isAddMode: false,
      searchDone: false,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isValidSsn() {
      const pattern = /^\d{3}-\d{6}$/; // ej: 660-355157
      return pattern.test((this.ssnName || '').trim());
    },
    pagination() {
      const c = this.contractors || {};
      return {
        currentPage: c.currentPage ?? 1,
        totalItemCount: c.totalItemCount ?? 0,
        pageCount: c.pageCount ?? 0,
        count: c.count ?? 0,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    },
    results() {
      // no mutar la prop: clonar + establecer checked por defecto
      const list = (this.contractors?.data ?? []).map((x) => ({
        ...x,
        checked: x.checked ?? false,
      }));
      return list;
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    showNoResults() {
      return this.searchDone && this.filteredResults.length === 0;
    },

    showAddLink() {
      return this.showNoResults && !this.isAddMode;
    },
    selectedIds() {
      return new Set(this.model.map((x) => x.id));
    },

    filteredResults() {
      return this.results.filter((r) => !this.selectedIds.has(r.id));
    },
  },
  methods: {
    onToggleAddForm() {
      this.isAddMode = !this.isAddMode;
    },
    onAddNewContractor(newContractor) {
      this.model = [...this.model, { ...newContractor, checked: true }];
      this.isAddMode = false;
      this.ssnName = '';
    },
    goToPageHandler(page) {
      this.$emit('load-contractors', {
        ssnName: this.ssnName,
        currentPage: page,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    },
    searchByCriteria() {
      this.searchDone = true;
      this.isAddMode = false;
      this.$emit('load-contractors', {
        ssn: this.isValidSsn ? this.ssnName : null,
        name: !this.isValidSsn ? this.ssnName : null,
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    },
    onSsnInput() {
      this.searchDone = false;
    },
    onToggleFromResults({ id, checked }) {
      if (!checked) return;
      const item = this.results.find((r) => r.id === id);
      if (item && !this.selectedIds.has(id)) {
        this.model = [...this.model, { ...item, checked: true }];
      }
    },
    onToggleFromSelected({ id, checked }) {
      if (checked) return;
      this.model = this.model.filter((s) => s.id !== id);
    },
    onSelectAllResults() {
      const toAdd = this.filteredResults.map((x) => ({ ...x, checked: true }));
      if (toAdd.length) this.model = [...this.model, ...toAdd];
    },
    onDeselectAllSelected() {
      this.model = [];
    },
  },
};
</script>

<style scoped lang="scss"></style>
